import { Form, Input, Modal, Space, Spin, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';

import { buildIdNameMaps, type PortfolioProjectRow } from '@/api/portfolio/api';
import type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
import { appRoutes, isNewTabClick, navigateTo } from '@/app/routes';
import { SharePointLinkResolver } from '@/components/common/SharePointLinkResolver';
import {
  isDirectorOrHigher,
  isProductOrProjectManager,
} from '@/components/pages/portfolio/portfolio_table.utils';
import { PortfolioExportButton } from '@/components/pages/portfolio/PortfolioExportButton';
import { PortfolioFilters } from '@/components/pages/portfolio/PortfolioFilters';
import { PortfolioStatCards } from '@/components/pages/portfolio/PortfolioStatCards';
import { PortfolioTable } from '@/components/pages/portfolio/PortfolioTable';
import { useAuthContext } from '@/hooks/auth.store';
import { useDevRoleOverride } from '@/hooks/useDevRoleOverride';
import { usePortfolio } from '@/hooks/usePortfolio';
import { useProjectSharePointEdit } from '@/hooks/useProjectSharePointEdit';

const { Paragraph, Text, Title } = Typography;

/**
 * Portfolio Dashboard (`/home`) — the Delivery Tracker portfolio overview.
 * Renders the health summary cards, filter bar, and filterable/sortable project
 * table. Backed by the real `GET /api/v1/portfolio/projects` endpoint (spec 0099),
 * fetched once; filtering, sorting, pagination, stats, and the filter dropdown
 * options are all derived client-side (see `usePortfolio`).
 */
interface PortfolioDashboardPageProps {
  onClientNamesResolved?: (names: Record<string, string>) => void;
  onProjectNamesResolved?: (names: Record<string, string>) => void;
}

export const PortfolioDashboardPage: React.FC<PortfolioDashboardPageProps> = ({
  onClientNamesResolved,
  onProjectNamesResolved,
}) => {
  const { accessToken, microsoftOboAssertion, user } = useAuthContext();
  const { roleState } = useDevRoleOverride();
  const directorOrHigher = useMemo(() => isDirectorOrHigher(user), [user]);
  const hasMetricAccess = useMemo(() => {
    if (roleState) {
      const isDirector = roleState.level === 'director' || roleState.level === 'vp';
      const isPM =
        roleState.role === 'pm' ||
        roleState.role === 'project_management' ||
        roleState.role === 'client_management';
      return isDirector || isPM;
    }
    return isDirectorOrHigher(user) || isProductOrProjectManager(user);
  }, [roleState, user]);

  const portfolio = usePortfolio({ accessToken, isDirectorOrHigher: directorOrHigher });

  useEffect(() => {
    if (portfolio.allRows.length === 0) {
      return;
    }
    const { clientNameById, projectNameById } = buildIdNameMaps(portfolio.allRows);
    onClientNamesResolved?.(clientNameById);
    onProjectNamesResolved?.(projectNameById);
  }, [portfolio.allRows, onClientNamesResolved, onProjectNamesResolved]);

  const {
    isEditModalOpen,
    editingRow,
    isEditLoading,
    isEditSubmitting,
    editProjectForm,
    messageContext,
    openEditProject,
    closeEditModal,
    handleSharePointResolved,
    handleEditProjectSubmit,
  } = useProjectSharePointEdit();

  const handleRowClick = useCallback(
    (row: PortfolioProjectRow, event?: React.MouseEvent<HTMLElement>) => {
      const url = appRoutes.projectHome(row.client_id, row.id);
      if (event && isNewTabClick(event)) {
        window.open(url, '_blank', 'noopener');
        return;
      }
      navigateTo(url);
    },
    [],
  );

  const handleClientClick = useCallback((clientId: string) => {
    navigateTo(appRoutes.projects(clientId));
  }, []);

  const summary = `${portfolio.total} active project${portfolio.total === 1 ? '' : 's'} across ${
    portfolio.filterOptions.clients.length
  } client${
    portfolio.filterOptions.clients.length === 1 ? '' : 's'
  } · Health rolled up from on-schedule, CSAT, team health.`;

  return (
    <>
      {messageContext}

      <Space direction="vertical" size="large" className="page-stack pf-page">
        <div className="pf-page__header">
          <div className="pf-page__heading">
            <Title level={3} className="pf-page__title">
              Portfolio overview
            </Title>
            <Text type="secondary" className="pf-page__subtitle">
              {summary}
            </Text>
          </div>
          {hasMetricAccess && (
            <PortfolioExportButton rows={portfolio.allRows} isDirectorOrHigher={directorOrHigher} />
          )}
        </div>

        <PortfolioStatCards
          stats={portfolio.stats}
          activePreset={portfolio.status}
          onSelect={portfolio.applyStatusPreset}
        />

        {portfolio.loading && portfolio.allRows.length === 0 ? (
          <div className="pf-filters pf-filters--loading">
            <Spin size="small" />
            <Text type="secondary">Loading filters…</Text>
          </div>
        ) : (
          <PortfolioFilters
            options={portfolio.filterOptions}
            filters={portfolio.filters}
            onClientChange={portfolio.setClientFilter}
            onProjectChange={portfolio.setProjectFilter}
            onOfferingChange={portfolio.setOfferingFilter}
            onLeaderChange={portfolio.setLeaderFilter}
            onSowStatusChange={portfolio.setSowStatus}
            onHealthChange={portfolio.setHealthFilter}
            onSearchChange={portfolio.setProjectSearch}
            onDateRangeChange={portfolio.setDateRange}
            onClearAll={portfolio.clearAllFilters}
          />
        )}

        <PortfolioTable
          rows={portfolio.rows}
          total={portfolio.total}
          page={portfolio.page}
          perPage={portfolio.perPage}
          loading={portfolio.loading}
          error={portfolio.error}
          sortBy={portfolio.sortBy}
          sortDir={portfolio.sortDir}
          onChangePage={portfolio.changePage}
          onChangeSort={portfolio.setSort}
          onRetry={portfolio.retry}
          onRowClick={handleRowClick}
          onClientClick={handleClientClick}
          onAddSharePointLink={openEditProject}
        />

        <div className="pf-page__footer">
          <span className="pf-page__legend">
            <span className="pf-missing-badge" aria-hidden="true">
              ⚠
            </span>
            <Text type="secondary">Missing = weekly update older than 7 days</Text>
          </span>
          <Text type="secondary" className="pf-page__source">
            <span className="pf-page__source-dot" aria-hidden="true" />
            Source: HubSpot · OpenAir · Snowflake
          </Text>
        </div>
      </Space>

      <Modal
        title={`Edit Project — ${editingRow ? editingRow.project_name : ''}`}
        open={isEditModalOpen}
        onCancel={closeEditModal}
        onOk={() => void handleEditProjectSubmit()}
        okText="Save Changes"
        confirmLoading={isEditSubmitting}
        width={760}
        destroyOnHidden
      >
        {isEditLoading ? (
          <Text type="secondary">Loading project…</Text>
        ) : (
          <Form form={editProjectForm} layout="vertical">
            <Title level={5}>Microsoft 365 (SharePoint folder)</Title>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              Paste a sharing link and choose Resolve. The API calls Microsoft Graph using app
              credentials (see infrastructure docs). Save to persist the folder binding.
            </Paragraph>
            <Form.Item name="sharepoint_site_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sharepoint_drive_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sharepoint_folder_item_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sharepoint_folder_web_url" hidden>
              <Input />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const webUrl = (
                  editProjectForm.getFieldValue('sharepoint_folder_web_url') as string | undefined
                )?.trim();
                const resolved: ResolveSharePointSharingLinkResponseDTO | null = webUrl
                  ? {
                      sharepoint_site_id:
                        (editProjectForm.getFieldValue('sharepoint_site_id') as
                          string | undefined) ?? '',
                      sharepoint_drive_id:
                        (editProjectForm.getFieldValue('sharepoint_drive_id') as
                          string | undefined) ?? '',
                      sharepoint_folder_item_id:
                        (editProjectForm.getFieldValue('sharepoint_folder_item_id') as
                          string | undefined) ?? '',
                      sharepoint_folder_web_url: webUrl,
                    }
                  : null;

                return (
                  <SharePointLinkResolver
                    accessToken={accessToken}
                    microsoftOboAssertion={microsoftOboAssertion}
                    clientId={editingRow?.client_id ?? ''}
                    projectId={editingRow?.id ?? ''}
                    resolved={resolved}
                    onResolved={handleSharePointResolved}
                  />
                );
              }}
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default PortfolioDashboardPage;
